import { useMemo } from "react";
import { TagKind } from "@stubhub/broadway-web";
import {
  ClockIcon,
  DollarCircleOutlineIcon,
  SparkleIcon,
  TicketsOutlineIcon,
  TrendOutlineIcon,
} from "@stubhub/broadway-web/icons/functional";
import type { EventGridListItemTagDescriptor } from "@stubhub/ensemble-discovery";
import { useSimpleFeatureTernary } from "@viagogo/feature";
import type { EventListGridItem, ISiteMessage } from "src/http/types";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";

type DescriptorKind = Exclude<TagKind, TagKind.Custom>;

const TAG_KIND_MAP: Record<string, DescriptorKind> = {
  Accent: TagKind.Accent,
  Neutral: TagKind.Neutral,
  Brand: TagKind.Brand,
  Info: TagKind.Info,
  Success: TagKind.Success,
  Warning: TagKind.Warning,
  Error: TagKind.Error,
};

const makeTag = (
  message: Maybe<ISiteMessage> | undefined,
  kind: DescriptorKind,
  icon: EventGridListItemTagDescriptor["icon"],
  hideDisclaimer: boolean,
): EventGridListItemTagDescriptor | null => {
  if (!message?.hasValue || !message.message) return null;
  const content = !hideDisclaimer && message.qualifier ? `${message.message} ${message.qualifier}` : message.message;
  return {
    content,
    kind,
    icon,
    qualifierContent: hideDisclaimer ? undefined : (message.disclaimer ?? undefined),
  };
};

/**
 * Returns a single merchandising tag descriptor for recently viewed event cards.
 *
 * Uses a custom priority order tuned for re-engagement (price signals first, urgency second).
 * Intentionally separate from the main useMerchMessageTags hook to avoid messages
 * irrelevant to returning users (e.g. instantDownload).
 */
export const useRecentlyViewedMerchTag = (event: EventListGridItem): EventGridListItemTagDescriptor[] => {
  const hideDisclaimer =
    useSimpleFeatureTernary(FeatureSetEnum.TMPerformerHideDisclaimer, false, true, undefined, false) ?? false;

  return useMemo(() => {
    const newListingsMessage = event.newListingsAddedMessage;
    const newListingsKind = newListingsMessage?.tagKind
      ? (TAG_KIND_MAP[newListingsMessage.tagKind] ?? TagKind.Info)
      : TagKind.Info;
    const tag =
      makeTag(newListingsMessage, newListingsKind, SparkleIcon, hideDisclaimer) ??
      makeTag(event.lowestPriceInThePastWeekMessage, TagKind.Success, DollarCircleOutlineIcon, hideDisclaimer) ??
      makeTag(event.lastMinuteDealMessage, TagKind.Accent, ClockIcon, hideDisclaimer) ??
      makeTag(event.fastSellingEventMessage, TagKind.Accent, TrendOutlineIcon, hideDisclaimer) ??
      makeTag(event.ticketPercentRemainingMessage, TagKind.Accent, TicketsOutlineIcon, hideDisclaimer);

    return tag ? [tag] : [];
  }, [event, hideDisclaimer]);
};
